/**
 * Saldo inicial/final por mes de una cuenta, computado en vivo por fin-api
 * desde transacciones + transferencias (GET /accounts/:id/monthly-balances).
 * Compartido entre Cuentas (historial), Transacciones y Análisis.
 */
import { useQuery, useQueries } from "@tanstack/react-query";
import clsx from "clsx";
import { api } from "@/lib/api";
import { formatMxnCents } from "@/lib/format";

export interface MonthlyBalance {
  period: string; // YYYY-MM (mes calendario) o YYYY-MM-DD (fecha de corte)
  openingCents: number;
  inflowCents: number;
  outflowCents: number;
  closingCents: number;
}

export interface MonthlyBalancesResponse {
  /** "cut" cuando la cuenta es TDC con día de corte y se pidió ?by=cut. */
  mode: "month" | "cut";
  months: MonthlyBalance[];
}

export function useMonthlyBalances(
  accountId: string | null | undefined,
  enabled = true,
  by: "month" | "cut" = "month",
) {
  return useQuery<MonthlyBalancesResponse>({
    queryKey: ["finance", "monthly-balances", accountId, by],
    queryFn: () =>
      api.get(`/finance/accounts/${accountId}/monthly-balances${by === "cut" ? "?by=cut" : ""}`),
    enabled: enabled && !!accountId,
  });
}

function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days)).toISOString().slice(0, 10);
}

interface PeriodBalanceRow {
  accountId: string;
  name: string;
  type: string;
  /** Solo cuentas bank/cash/other lo traen. */
  currentBalanceCents?: number;
  /** Solo credit_card: deuda al momento (positiva). */
  usedCents?: number;
}

/**
 * Saldo firmado comparable entre tipos de cuenta: bank/cash/other tal
 * cual; TDC en convención negativo = deuda / positivo = a favor. Loans
 * (sin ninguno de los dos campos) quedan fuera → null.
 */
function signedBalanceCents(b: PeriodBalanceRow): number | null {
  if (b.currentBalanceCents != null) return b.currentBalanceCents;
  if (b.usedCents != null) return -b.usedCents;
  return null;
}

function usePeriodBalances(asOf: string, enabled: boolean) {
  return useQuery<{ balances: PeriodBalanceRow[] }>({
    queryKey: ["finance", "balances", "asOf", asOf],
    queryFn: () => api.get(`/finance/balances?asOf=${asOf}`),
    enabled,
  });
}

/**
 * Saldo inicial → final de cada cuenta bancaria para un rango de fechas
 * arbitrario (los filtros de Transacciones). Inicial = saldo al día
 * anterior a `from`; final = saldo al cierre de `to`. Reusa /balances?asOf,
 * misma regla de signo que el running balance. Si hay filtro de cuenta,
 * muestra solo esa. Sin rango completo no renderea nada.
 */
export function PeriodBalancesStrip({
  from,
  to,
  accountId,
}: {
  from: string;
  to: string;
  accountId?: string;
}) {
  const hasRange = !!from && !!to;
  const opening = usePeriodBalances(hasRange ? addDaysIso(from, -1) : "", hasRange);
  const closing = usePeriodBalances(to, hasRange);
  if (!hasRange) return null;

  const openingById = new Map(
    (opening.data?.balances ?? []).map((b) => [b.accountId, signedBalanceCents(b)]),
  );
  const rows = (closing.data?.balances ?? []).filter(
    (b) => signedBalanceCents(b) != null && (!accountId || b.accountId === accountId),
  );
  if (rows.length === 0) return null;

  return (
    <div className="card-elevated px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-base-content/55 font-semibold mb-2">
        Saldos del periodo
      </div>
      <div className="flex flex-wrap gap-x-8 gap-y-2">
        {rows.map((b) => {
          const openCents = openingById.get(b.accountId) ?? 0;
          const closeCents = signedBalanceCents(b) ?? 0;
          const delta = closeCents - openCents;
          const isCredit = b.usedCents != null;
          return (
            <div key={b.accountId} className="text-sm">
              <div className="text-xs text-base-content/60">
                {b.name}
                {isCredit && (
                  <span className="text-base-content/40"> · negativo = deuda</span>
                )}
              </div>
              <div className="tabular-nums flex items-baseline gap-1.5 flex-wrap">
                <span className="text-base-content/65" title={`Saldo inicial (al ${addDaysIso(from, -1)})`}>
                  {formatMxnCents(openCents)}
                </span>
                <span className="text-base-content/40">→</span>
                <span
                  className={clsx("font-semibold", closeCents < 0 && "text-error")}
                  title={`Saldo final (al ${to})`}
                >
                  {formatMxnCents(closeCents)}
                </span>
                <span
                  className={clsx(
                    "text-[11px]",
                    delta > 0 ? "text-success" : delta < 0 ? "text-error" : "text-base-content/45",
                  )}
                >
                  {delta >= 0 ? "+" : ""}
                  {formatMxnCents(delta)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Tabla meses × cuentas: una columna por cuenta con saldo inicial (muted)
 * y final (bold) apilados — así cabe en móvil sin 2N columnas. Meses en
 * orden descendente. Un mes sin movimientos en una cuenta se muestra "—"
 * (el saldo se arrastra al siguiente mes con actividad).
 */
export function MonthlyBalancesTable({
  accounts,
}: {
  accounts: Array<{ id: string; name: string }>;
}) {
  const queries = useQueries({
    queries: accounts.map((a) => ({
      queryKey: ["finance", "monthly-balances", a.id, "month"],
      queryFn: () => api.get(`/finance/accounts/${a.id}/monthly-balances`),
    })),
  }) as Array<{ data?: MonthlyBalancesResponse; isLoading: boolean }>;

  const isLoading = queries.some((q) => q.isLoading);
  const byAccount = accounts.map((a, i) => ({
    account: a,
    months: new Map((queries[i]?.data?.months ?? []).map((m) => [m.period, m])),
  }));
  const periods = [
    ...new Set(byAccount.flatMap((b) => [...b.months.keys()])),
  ].sort((a, b) => b.localeCompare(a));

  if (isLoading) {
    return <div className="text-sm text-base-content/55 py-4 text-center">Cargando…</div>;
  }
  if (periods.length === 0) {
    return (
      <div className="text-sm text-base-content/55 py-4 text-center">
        Sin movimientos registrados.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto max-h-96 overflow-y-auto">
      <table className="table table-xs table-pin-rows">
        <thead>
          <tr>
            <th className="text-left">Mes</th>
            {accounts.map((a) => (
              <th key={a.id} className="text-right">
                {a.name}
                <div className="font-normal text-[10px] text-base-content/45">
                  inicial / final
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {periods.map((p) => (
            <tr key={p}>
              <td className="font-mono text-base-content/65">{p}</td>
              {byAccount.map(({ account, months }) => {
                const m = months.get(p);
                return (
                  <td key={account.id} className="text-right tabular-nums">
                    {m ? (
                      <div
                        title={`Entradas ${formatMxnCents(m.inflowCents)} · Salidas ${formatMxnCents(m.outflowCents)}`}
                      >
                        <div className="text-base-content/55 text-[11px]">
                          {formatMxnCents(m.openingCents)}
                        </div>
                        <div
                          className={clsx(
                            "font-medium",
                            m.closingCents < 0 ? "text-error" : "text-base-content",
                          )}
                        >
                          {formatMxnCents(m.closingCents)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-base-content/30">—</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
